// "Meist" storyline (sections/story-timeline.liquid): a port of the "Timeline" React component
// (Hyperiux Vault) to plain JS. Same GSAP timelines and SplitText line reveal. The original has
// 7 hand-tuned steps, this section has as many as there are blocks, so the slide distance, the
// line length and the reveal start/end are measured from the real layout instead: the slider
// stops where the last step still fits next to the right content edge, and the line ends with its
// dot exactly on that edge (the page gutter, like every other section).
//
// The pinned look comes from CSS (position: sticky) and not from ScrollTrigger pinning. On screens
// from 990px the theme scrolls .page-wrapper instead of the window (assets/base.css), so that is
// the scroller ScrollTrigger has to watch there.
(() => {
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const PHONE = window.matchMedia('(max-width: 599px)');
  const PAGE_SCROLLS = window.matchMedia('(min-width: 990px)');

  // The original's reveal duration, in seconds.
  const DURATION = 1.4;

  const init = (/** @type {HTMLElement} */ section) => {
    const { gsap, ScrollTrigger, SplitText } = /** @type {any} */ (window);
    if (!gsap || !ScrollTrigger || !SplitText) return;
    gsap.registerPlugin(ScrollTrigger, SplitText);

    const slider = section.querySelector('[data-tl-slider]');
    const axisLine = section.querySelector('[data-tl-axis-line]');
    if (!slider || !axisLine) return;

    const elements = [...section.querySelectorAll('[data-tl-item]')].sort(
      (a, b) => Number(a.dataset.index) - Number(b.dataset.index)
    );
    const items = elements.map((element) => ({
      element,
      isTop: element.classList.contains('tl__item--top'),
      line: element.querySelector('.tl__line'),
      dot: element.querySelector('.tl__dot'),
      title: element.querySelector('.tl__title'),
      desc: element.querySelector('.tl__desc'),
    }));
    if (items.length === 0) return;

    const pageWrapper = document.querySelector('.page-wrapper');
    const scroller = PAGE_SCROLLS.matches && pageWrapper ? pageWrapper : window;
    const viewportHeight = scroller === window ? window.innerHeight : /** @type {HTMLElement} */ (scroller).clientHeight;

    const isMobile = PHONE.matches;
    const lineStart = isMobile ? 'top 30%' : 'top 25%';
    const slideEnd = isMobile ? '82% 50%' : '92% bottom';
    const lineEnd = isMobile ? '80% 50%' : '92% bottom';

    // ---- Measure the untransformed layout (relative to the section's left edge)
    const sectionLeft = section.getBoundingClientRect().left;
    const viewWidth = section.clientWidth;
    const vw = window.innerWidth / 100;
    const gutter = parseFloat(getComputedStyle(slider).paddingLeft) || 0;
    const rightEdge = viewWidth - gutter;
    const leftOf = (/** @type {Element} */ element) => element.getBoundingClientRect().left - sectionLeft;
    const startDot = /** @type {HTMLElement} */ (section.querySelector('.tl__axis-dot'));
    const dotSize = startDot.offsetWidth;
    const axisLeft = leftOf(axisLine) - dotSize;
    const stemX = items.map((item) => leftOf(item.element));

    // Slide until the last step's stem is one step width (text included) from the right edge.
    const lastStemTarget = rightEdge - (isMobile ? 66 : 26) * vw;
    const slide = Math.max(0, stemX[stemX.length - 1] - lastStemTarget);
    // Line long enough for its end dot to sit on the right edge once the slider has moved.
    const lineWidth = Math.max(0, rightEdge + slide - axisLeft - 2 * dotSize);

    // Scroll distance (px from the section's top) over which the slider moves.
    const sectionHeight = section.offsetHeight;
    const slideRange = Math.max(
      1,
      isMobile ? 0.82 * sectionHeight - 0.5 * viewportHeight : 0.92 * sectionHeight - viewportHeight
    );
    const speed = slide / slideRange; // px the slider moves per px of scrolling

    /** "top+=120 top": the section's top (+ offset) meets the scroller's top. */
    const at = (/** @type {number} */ offset) => (offset >= 0 ? `top+=${offset} top` : `top-=${-offset} top`);
    /** Scroll offset at which a stem that starts at x reaches screen position `screenX`. */
    const scrollWhenAt = (/** @type {number} */ x, /** @type {number} */ screenX) =>
      speed > 0 ? Math.min(slideRange, (x - screenX) / speed) : 0;

    /** @type {Array<{ revert: () => void }>} */
    const splits = [];

    const context = gsap.context(() => {
      const base = { scroller };

      // Slider slides sideways with the scroll.
      gsap
        .timeline({
          scrollTrigger: { ...base, trigger: section, start: 'top top', end: slideEnd, scrub: true },
          defaults: { ease: 'none' },
        })
        .fromTo(slider, { x: 0 }, { x: -slide });

      const dots = items.map((item) => item.dot);
      gsap.set(dots, { xPercent: -50 });

      if (REDUCED_MOTION.matches) {
        gsap.set(axisLine, { width: lineWidth });
        items.forEach((item) => {
          gsap.set(item.line, { scaleY: 1 });
          gsap.set(item.dot, { scale: 1 });
        });
        return;
      }

      // The line along the middle draws itself.
      gsap.to(axisLine, {
        width: lineWidth,
        ease: 'none',
        scrollTrigger: { ...base, trigger: section, start: lineStart, end: lineEnd, scrub: true },
      });

      items.forEach((item) => {
        gsap.set(item.line, { scaleY: 0, transformOrigin: item.isTop ? '50% 100%' : '50% 0%' });
        gsap.set(item.dot, { scale: 0 });
        // Text starts blurred and invisible and comes into focus with its reveal.
        gsap.set([item.title, item.desc], { opacity: 0, filter: 'blur(12px)' });
      });

      items.forEach((item, index) => {
        const titleSplit = new SplitText(item.title, { type: 'chars, words, lines', mask: 'lines' });
        const descSplit = new SplitText(item.desc, { type: 'chars, words, lines', mask: 'lines' });
        // The step reveals while its stem travels from about the right of the screen to the middle;
        // the last one can only get as far as the slider goes.
        const endX = Math.max(0.4 * viewWidth, stemX[index] - slide);
        const startX = Math.max(endX + 0.2 * viewWidth, Math.min(0.9 * viewWidth, endX + 0.45 * viewWidth));
        const duration = Math.max(0.2, DURATION);
        // Nothing reveals before the stage is pinned (scroll 0), and every step gets a stretch of
        // scrolling long enough to be seen, even the first ones that are already in view.
        const revealStart = Math.max(0, scrollWhenAt(stemX[index], startX));
        const revealEnd = Math.max(scrollWhenAt(stemX[index], endX), revealStart + 0.35 * viewportHeight);

        gsap
          .timeline({
            scrollTrigger: {
              ...base,
              trigger: section,
              start: at(revealStart),
              end: at(revealEnd),
              scrub: true,
            },
          })
          .to(item.line, { scaleY: 1, duration: duration * 0.4 })
          .to(item.dot, { scale: 1, duration: duration * 0.4 }, '<')
          .fromTo(
            titleSplit.lines,
            { y: 100 },
            { y: 0, delay: -0.8 * duration, duration, stagger: 0.02, ease: 'power2.out' }
          )
          .fromTo(
            [item.title, item.desc],
            { opacity: 0, filter: 'blur(12px)' },
            { opacity: 1, filter: 'blur(0px)', duration: duration * 0.6, ease: 'power2.out' },
            '<'
          )
          .fromTo(
            descSplit.lines,
            { y: 100 },
            { y: 0, duration, stagger: 0.02, ease: 'power2.out' },
            '<'
          );

        splits.push(titleSplit, descSplit);
      });

      // Runs when the context is reverted: puts the original text back.
      return () => splits.forEach((split) => split.revert());
    }, section);

    return context;
  };

  const start = () => {
    const sections = [...document.querySelectorAll('[data-timeline]')];
    /** @type {Map<HTMLElement, any>} */
    const contexts = new Map();

    const build = () => {
      for (const section of sections) {
        contexts.get(section)?.revert();
        contexts.set(section, init(section));
      }
      /** @type {any} */ (window).ScrollTrigger?.refresh();
    };

    // Rebuild when a breakpoint it depends on is crossed.
    PHONE.addEventListener('change', build);
    PAGE_SCROLLS.addEventListener('change', build);
    REDUCED_MOTION.addEventListener('change', build);
    // Distances are measured, so a new window width needs a new measurement. Phones fire resize
    // when the address bar hides; only the width counts.
    let lastWidth = window.innerWidth;
    let resizeTimer = 0;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        if (window.innerWidth === lastWidth) return;
        lastWidth = window.innerWidth;
        build();
      }, 200);
    });

    // Line breaks that SplitText measures depend on the fonts being loaded.
    (document.fonts?.ready ?? Promise.resolve()).then(build);
  };

  const run = () => {
    if (document.querySelector('[data-timeline]')) start();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
  document.addEventListener('shopify:section:load', run);
})();
